import { CountryCode } from '@client/constants';
import { useApiClient } from '@client/hooks/useApiClient';
import { useImageUpload, } from '@client/hooks/useImageUpload';
import { useShopManager } from '@client/providers/ShopManagerProvider';
import { callApi } from '@client/utils/apiUtils';
import { ImageUploadKind, } from '@heirloom/common/constants';
import { addVariationToCombinations, DEFAULT_COMBINATION, deriveCombinationsList, removeVariationFromCombinations, sortByOrder, syncVariationImages, syncVariationOptions, } from '@heirloom/common/domain/listing';
import { findInvalidCombinations, validateListingFields, } from '@heirloom/common/validation/listing';
import { ValidationField, } from '@heirloom/common/validation/shared';
import { useRef, useState } from 'react';
export const useListingForm = ({ shopShortId, initialTitle = '', initialSubtitle = '', initialCategoryId = null, initialCountryCode = null, initialPriceCents = null, initialTrackInventory = false, initialInventory = '', initialImageEntries, initialProcessingProfileId = null, initialShippingProfileId = null, initialReturnProfileId = null, initialProcessingProfiles = [], initialShippingProfiles = [], initialReturnProfiles = [], initialPersonalizationProfileId = null, initialPersonalizationProfiles = [], initialDescrSections = [], initialVariations = {}, initialCombinations = {}, initialVariationImages = null, }) => {
    const apiClient = useApiClient();
    const { directFulfillment } = useShopManager();
    const [title, setTitle] = useState(initialTitle);
    const [titleError, setTitleError] = useState(null);
    const [subtitle, setSubtitle] = useState(initialSubtitle);
    const [subtitleError, setSubtitleError] = useState(null);
    const [categoryId, setCategoryId] = useState(initialCategoryId);
    const [categoryError, setCategoryError] = useState(null);
    const [countryCode, setCountryCode] = useState(initialCountryCode ?? CountryCode.US);
    const [countryError, setCountryError] = useState(null);
    const [imageError, setImageError] = useState(null);
    const [priceCents, setPriceCents] = useState(initialPriceCents);
    const [priceError, setPriceError] = useState(null);
    const [trackInventory, setTrackInventory] = useState(initialTrackInventory);
    const [inventory, setInventory] = useState(initialInventory);
    const [combinationError, setCombinationError] = useState(null);
    const [invalidCombinationKeys, setInvalidCombinationKeys] = useState(new Set());
    const [processingProfiles, setProcessingProfiles] = useState(initialProcessingProfiles);
    const addProcessingProfile = (profile) => setProcessingProfiles((prev) => [...prev, profile]);
    const [processingProfileId, setProcessingProfileId] = useState(initialProcessingProfileId);
    const [processingProfileError, setProcessingProfileError] = useState(null);
    const [shippingProfiles, setShippingProfiles] = useState(initialShippingProfiles);
    const addShippingProfile = (profile) => setShippingProfiles((prev) => [...prev, profile]);
    const [shippingProfileId, setShippingProfileId] = useState(initialShippingProfileId);
    const [shippingProfileError, setShippingProfileError] = useState(null);
    const [returnProfiles, setReturnProfiles] = useState(initialReturnProfiles);
    const addReturnProfile = (profile) => setReturnProfiles((prev) => [...prev, profile]);
    const [returnProfileId, setReturnProfileId] = useState(initialReturnProfileId);
    const [returnProfileError, setReturnProfileError] = useState(null);
    const [personalizationProfiles, setPersonalizationProfiles] = useState(initialPersonalizationProfiles);
    const addPersonalizationProfile = (profile) => setPersonalizationProfiles((prev) => [...prev, profile]);
    const [personalizationProfileId, setPersonalizationProfileId] = useState(initialPersonalizationProfileId);
    const [variations, setVariations] = useState(initialVariations);
    const [variationImages, setVariationImages] = useState(() => syncVariationImages(initialVariationImages, initialVariations));
    const applyVariations = (next) => {
        setVariations(next);
        setVariationImages((prev) => syncVariationImages(prev, next));
    };
    const addVariation = (variation) => {
        const newId = crypto.randomUUID();
        applyVariations({ ...variations, [newId]: variation });
        setCombinations((prev) => addVariationToCombinations(prev, newId, variation.options, variation.pricesVary));
    };
    const removeVariation = (id) => {
        const next = { ...variations };
        delete next[id];
        applyVariations(next);
        setCombinations((prev) => removeVariationFromCombinations(prev, id));
    };
    const updateVariation = (id, variation) => {
        const oldOptionIds = Object.keys(variations[id]?.options ?? {});
        const newOptionIds = Object.keys(variation.options);
        applyVariations({ ...variations, [id]: variation });
        setCombinations((prev) => syncVariationOptions(prev, id, oldOptionIds, newOptionIds));
    };
    const [combinations, setCombinations] = useState(initialCombinations);
    const setVariationImage = (key, imageUuid) => {
        setVariationImages((prev) => prev && key in prev ? { ...prev, [key]: imageUuid } : prev);
    };
    const setCombinationField = (key, patch) => {
        setCombinations((prev) => ({
            ...prev,
            [key]: { ...DEFAULT_COMBINATION, ...prev[key], ...patch },
        }));
    };
    const reorderVariations = (fromId, toId) => {
        setVariations((prev) => {
            const sorted = sortByOrder(prev);
            const fromIndex = sorted.findIndex(([id]) => id === fromId);
            const toIndex = sorted.findIndex(([id]) => id === toId);
            const [item] = sorted.splice(fromIndex, 1);
            sorted.splice(toIndex, 0, item);
            return Object.fromEntries(sorted.map(([id, v], i) => [id, { ...v, order: i }]));
        });
    };
    const [descrSections, setDescrSections] = useState(initialDescrSections);
    const [descrSectionIds, setDescrSectionIds] = useState(initialDescrSections.map(() => crypto.randomUUID()));
    const addDescrSection = (section) => {
        setDescrSections((prev) => [...prev, section]);
        setDescrSectionIds((prev) => [...prev, crypto.randomUUID()]);
    };
    const updateDescrSection = (index, section) => {
        setDescrSections((prev) => prev.map((s, i) => (i === index ? section : s)));
    };
    const removeDescrSection = (index) => {
        setDescrSections((prev) => prev.filter((_, i) => i !== index));
        setDescrSectionIds((prev) => prev.filter((_, i) => i !== index));
    };
    const reorderDescrSections = (fromIndex, toIndex) => {
        const move = (arr) => {
            const result = [...arr];
            const [item] = result.splice(fromIndex, 1);
            result.splice(toIndex, 0, item);
            return result;
        };
        setDescrSections(move);
        setDescrSectionIds(move);
    };
    const { imageEntries, addFiles, removeImage, reorderImages, uploadImage, isUploading, uuids, } = useImageUpload(async (contentType) => {
        const result = await callApi(apiClient.shops.getListingImageUploadUrl({
            params: { id: shopShortId },
            body: { contentType },
        }));
        return result.error !== null ? null : result.data;
    }, ImageUploadKind.LISTING, initialImageEntries);
    // Dirty tracking: snapshot the user-editable fields on mount and
    // compare against the current values each render.
    const serializeFields = () => JSON.stringify({
        title,
        subtitle,
        categoryId,
        countryCode,
        priceCents,
        trackInventory,
        inventory,
        imageUuids: imageEntries.map((e) => e.uuid),
        processingProfileId,
        shippingProfileId,
        returnProfileId,
        personalizationProfileId,
        descrSections,
        variations,
        combinations,
        variationImages,
    });
    const initialSnapshot = useRef(null);
    if (initialSnapshot.current === null)
        initialSnapshot.current = serializeFields();
    const isDirty = serializeFields() !== initialSnapshot.current;
    const validate = () => {
        const errors = validateListingFields({
            title,
            subtitle,
            categoryId,
            countryCode,
            priceCents,
            trackInventory,
            inventory: inventory === '' ? null : Number(inventory),
            imageUuids: uuids,
            shippingProfileId,
            returnProfileId,
            processingProfileId,
            fullDescr: descrSections,
            variations,
            combinations,
            variationImages,
        }, { directFulfillment });
        const findError = (field) => errors.find((e) => e.field === field)?.message ?? null;
        setTitleError(findError(ValidationField.Title));
        setSubtitleError(findError(ValidationField.Subtitle));
        setCategoryError(findError(ValidationField.Category));
        setCountryError(findError(ValidationField.Country));
        setImageError(findError(ValidationField.Images));
        setShippingProfileError(findError(ValidationField.ShippingProfile));
        setReturnProfileError(findError(ValidationField.ReturnProfile));
        setProcessingProfileError(findError(ValidationField.ProcessingProfile));
        const pricesVary = Object.values(variations).some((v) => v.pricesVary);
        setPriceError(pricesVary ? null : findError(ValidationField.Price));
        const { hasActive, missingPriceKeys } = findInvalidCombinations(variations, combinations, priceCents, trackInventory);
        const hasCombinations = deriveCombinationsList(variations).length > 0;
        if (hasCombinations && !hasActive) {
            setCombinationError('At least one combination must be active.');
            setInvalidCombinationKeys(new Set());
        }
        else if (missingPriceKeys.length > 0) {
            setCombinationError('Price is required for every combination.');
            setInvalidCombinationKeys(new Set(missingPriceKeys));
        }
        else {
            setCombinationError(null);
            setInvalidCombinationKeys(new Set());
        }
        return errors.length === 0;
    };
    return {
        shopShortId,
        title,
        setTitle,
        titleError,
        setTitleError,
        subtitle,
        setSubtitle,
        subtitleError,
        setSubtitleError,
        categoryId,
        setCategoryId,
        categoryError,
        setCategoryError,
        countryCode,
        setCountryCode,
        countryError,
        setCountryError,
        imageEntries,
        addImageFiles: addFiles,
        removeImage,
        reorderImageEntries: reorderImages,
        uploadImage,
        isUploadingImages: isUploading,
        uploadedUuids: uuids,
        imageError,
        setImageError,
        priceCents,
        setPriceCents,
        priceError,
        setPriceError,
        trackInventory,
        setTrackInventory,
        inventory,
        setInventory,
        combinationError,
        setCombinationError,
        invalidCombinationKeys,
        setInvalidCombinationKeys,
        processingProfiles,
        addProcessingProfile,
        processingProfileId,
        setProcessingProfileId,
        processingProfileError,
        setProcessingProfileError,
        shippingProfiles,
        addShippingProfile,
        shippingProfileId,
        setShippingProfileId,
        shippingProfileError,
        setShippingProfileError,
        returnProfiles,
        addReturnProfile,
        returnProfileId,
        setReturnProfileId,
        returnProfileError,
        setReturnProfileError,
        personalizationProfiles,
        addPersonalizationProfile,
        personalizationProfileId,
        setPersonalizationProfileId,
        descrSections,
        descrSectionIds,
        addDescrSection,
        updateDescrSection,
        removeDescrSection,
        reorderDescrSections,
        variations,
        addVariation,
        removeVariation,
        updateVariation,
        reorderVariations,
        combinations,
        variationImages,
        setVariationImage,
        setCombinationField,
        isDirty,
        validate,
    };
};
